import { useMemo, useState } from 'react';
import { formatBytes } from '../lib/format';
import type { ConflictPolicy, IncomingOffer } from '../transfer/types';

export function OfferCard({ offer, canReceive, onAccept, onDecline }: {
  offer: IncomingOffer;
  canReceive: boolean;
  onAccept: (offer: IncomingOffer, fileIds: string[], policy: ConflictPolicy) => void;
  onDecline: (offer: IncomingOffer) => void;
}) {
  const [selected, setSelected] = useState(() => new Set(offer.files.map((file) => file.id)));
  const [policy, setPolicy] = useState<ConflictPolicy>('keep-both');
  const [visibleFiles, setVisibleFiles] = useState(100);
  const groups = useMemo(() => {
    const result = new Map<string, { ids: string[]; bytes: number }>();
    for (const file of offer.files) {
      const name = file.relativePath.split('/')[0];
      const group = result.get(name) ?? { ids: [], bytes: 0 };
      group.ids.push(file.id);
      group.bytes += file.size;
      result.set(name, group);
    }
    return [...result.entries()];
  }, [offer.files]);
  const selectedFiles = offer.files.filter((file) => selected.has(file.id));
  const selectedBytes = selectedFiles.reduce((total, file) => total + file.size, 0);

  function toggle(ids: string[], enabled: boolean) {
    setSelected((current) => {
      const next = new Set(current);
      ids.forEach((id) => enabled ? next.add(id) : next.delete(id));
      return next;
    });
  }

  return <article className="offer">
    <div><strong>Incoming files</strong><span>{offer.files.length} file{offer.files.length === 1 ? '' : 's'} · {formatBytes(offer.totalBytes)}</span></div>
    <p>{offer.topLevelNames.slice(0, 3).join(', ')}{offer.topLevelNames.length > 3 ? ` +${offer.topLevelNames.length - 3} more` : ''}</p>
    {!canReceive && <div className="alert warning"><strong>This device is send-only.</strong> Open this link in Chrome on Android or desktop Chrome/Edge to receive files.</div>}
    {canReceive && <details className="offer-selection">
      <summary>Choose files · {selectedFiles.length} selected ({formatBytes(selectedBytes)})</summary>
      <div className="group-selection">
        {groups.map(([name, group]) => {
          const count = group.ids.filter((id) => selected.has(id)).length;
          return <label key={name}><input type="checkbox" checked={count === group.ids.length} ref={(node) => { if (node) node.indeterminate = count > 0 && count < group.ids.length; }} onChange={(event) => toggle(group.ids, event.target.checked)} /><span><strong>{name}</strong><small>{group.ids.length} file{group.ids.length === 1 ? '' : 's'} · {formatBytes(group.bytes)}</small></span></label>;
        })}
      </div>
      <div className="file-selection-list">
        {offer.files.slice(0, visibleFiles).map((file) => <label key={file.id}><input type="checkbox" checked={selected.has(file.id)} onChange={(event) => toggle([file.id], event.target.checked)} /><span><strong>{file.relativePath}</strong><small>{formatBytes(file.size)}</small></span></label>)}
      </div>
      {visibleFiles < offer.files.length && <button className="button ghost compact" onClick={() => setVisibleFiles((count) => count + 100)}>Show 100 more files</button>}
    </details>}
    {canReceive && <label className="field compact-field">If a file already exists
      <select value={policy} onChange={(event) => setPolicy(event.target.value as ConflictPolicy)}>
        <option value="keep-both">Keep both with a numbered name</option>
        <option value="skip-existing">Skip the incoming file</option>
        <option value="replace">Replace the existing file</option>
        <option value="ask">Ask me for each conflict</option>
      </select>
    </label>}
    <div className="button-row">
      {canReceive && <button className="button primary" disabled={!selected.size && !offer.directories.length} onClick={() => onAccept(offer, [...selected], policy)}>Choose destination & accept</button>}
      <button className="button ghost" onClick={() => onDecline(offer)}>Decline</button>
    </div>
  </article>;
}
